/**
 * playback-speed — pure logic for the shared playback-speed control.
 *
 * One persisted preference drives three audio surfaces (MediaSegmentPlayer
 * card-back replay, VideoStudyPlayer whole-file, and TTS). UI lives in
 * SpeedSelect, state in useSettings.playbackSpeed; this module holds the
 * speed ladder, validation, and the TTS offset so it is unit-testable
 * without React or the speechSynthesis mock.
 */

/** Canonical speed ladder offered by the pill `<select>`. */
export const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const

/** Normal speed. Also the stored default when no pref is set. */
export const DEFAULT_SPEED = 1

const MIN_SPEED = SPEEDS[0]
const MAX_SPEED = SPEEDS[SPEEDS.length - 1]

/**
 * Validate + clamp a stored/raw speed value to a legal ladder step.
 * Non-finite or non-number input falls back to DEFAULT_SPEED. Values outside
 * the ladder clamp to the nearest endpoint; in-range non-ladder values (which
 * the `<select>` never emits but a hand-edited localStorage could) snap to the
 * nearest ladder step.
 */
export function clampSpeed(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_SPEED

  let nearest: number = SPEEDS[0]
  let nearestDist = Infinity
  for (const step of SPEEDS) {
    const dist = Math.abs(step - value)
    if (dist < nearestDist) {
      nearestDist = dist
      nearest = step
    }
  }
  // Range-violating values: nearest-step already lands on the closer endpoint,
  // but pin explicitly so 3 → MAX (not some interior step) is unambiguous.
  if (value > MAX_SPEED) return MAX_SPEED
  if (value < MIN_SPEED) return MIN_SPEED
  return nearest
}

/**
 * Map a media playback speed to a TTS utterance rate. TTS reads one notch
 * slower than media — preserves the historical 0.9 default at media 1× and
 * keeps pronunciation review intelligible at the slow end (floors at 0.5).
 */
export function ttsRateFor(speed: number): number {
  return Math.min(Math.max(speed - 0.1, 0.5), 2)
}
